/**
 * Feature Request Zod Validation Schemas
 */

import { z } from 'zod';

import { FEATURE_REQUEST_LIMITS } from '../types/models/feature-request.types';

/**
 * Attachment schema
 */
export const FeatureRequestAttachmentSchema = z.object({
  id: z.string().min(1, 'Attachment ID is required'),
  fileName: z.string().min(1, 'File name is required').max(255),
  mimeType: z.string().min(1, 'MIME type is required'),
  size: z
    .number()
    .positive('File size must be positive')
    .max(
      FEATURE_REQUEST_LIMITS.maxAttachmentSizeMB * 1024 * 1024,
      `File size cannot exceed ${FEATURE_REQUEST_LIMITS.maxAttachmentSizeMB}MB`
    ),
  blobPath: z.string().min(1, 'Blob path is required'),
  uploadedAt: z.string().min(1, 'Upload timestamp is required'),
});

/**
 * Create feature request schema
 */
export const CreateFeatureRequestSchema = z.object({
  title: z
    .string()
    .min(
      FEATURE_REQUEST_LIMITS.minTitleLength,
      `Title must be at least ${FEATURE_REQUEST_LIMITS.minTitleLength} characters`
    )
    .max(
      FEATURE_REQUEST_LIMITS.maxTitleLength,
      `Title cannot exceed ${FEATURE_REQUEST_LIMITS.maxTitleLength} characters`
    ),
  description: z
    .string()
    .min(
      FEATURE_REQUEST_LIMITS.minDescriptionLength,
      `Description must be at least ${FEATURE_REQUEST_LIMITS.minDescriptionLength} characters`
    )
    .max(
      FEATURE_REQUEST_LIMITS.maxDescriptionLength,
      `Description cannot exceed ${FEATURE_REQUEST_LIMITS.maxDescriptionLength} characters`
    ),
  attachments: z
    .array(FeatureRequestAttachmentSchema)
    .max(
      FEATURE_REQUEST_LIMITS.maxAttachments,
      `Cannot attach more than ${FEATURE_REQUEST_LIMITS.maxAttachments} files`
    )
    .optional(),
});

/**
 * Update status schema (admin)
 */
export const UpdateFeatureRequestStatusSchema = z.object({
  status: z.enum(['approved', 'rejected', 'under_review', 'in_progress', 'completed', 'declined']),
  reason: z.string().max(500, 'Reason cannot exceed 500 characters').optional(),
});

/**
 * Admin response schema
 */
export const AdminRespondSchema = z.object({
  response: z
    .string()
    .min(1, 'Response is required')
    .max(
      FEATURE_REQUEST_LIMITS.maxResponseLength,
      `Response cannot exceed ${FEATURE_REQUEST_LIMITS.maxResponseLength} characters`
    ),
});

/**
 * Vote schema
 */
export const VoteSchema = z.object({
  voteType: z.enum(['up', 'down']),
});

/**
 * List parameters schema
 */
export const ListFeatureRequestsParamsSchema = z.object({
  sortBy: z.enum(['upvotes', 'createdAt']).optional().default('upvotes'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  limit: z.coerce.number().min(1).max(100).optional().default(20),
  continuationToken: z.string().optional(),
});

/**
 * Admin list parameters schema
 */
export const AdminListFeatureRequestsParamsSchema = ListFeatureRequestsParamsSchema.extend({
  status: z
    .enum([
      'pending',
      'approved',
      'under_review',
      'rejected',
      'in_progress',
      'completed',
      'declined',
    ])
    .optional(),
});
